import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, RefreshCcw, Search } from "lucide-react";
import toast from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function ManageDistributors() {
  const [distributors, setDistributors] = useState([]);
  const [filteredDistributors, setFilteredDistributors] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    dor: "",
    phone: "",
    gender: "",
    idNumber: "",
    nationality: "",
    otherNationality: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 6;

  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = filteredDistributors.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredDistributors.length / recordsPerPage);

  // ✅ Fetch Distributors
  const fetchDistributors = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${SERVER_URL}/api/distributors`);
      const data = await res.json();
      if (res.ok) {
        setDistributors(data);
        setFilteredDistributors(data);
      } else toast.error(data.error || "Failed to load distributors.");
    } catch {
      toast.error("Server error while fetching distributors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributors();
  }, []);

  // ✅ Filter/Search
  useEffect(() => {
    const filtered = distributors.filter(
      (d) =>
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.phone.toString().includes(searchTerm) ||
        d.nationality.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredDistributors(filtered);
    setCurrentPage(1);
  }, [searchTerm, distributors]);

  // ✅ Handle Input Change
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // ✅ Validate Form
  const validateForm = () => {
    const phoneRegex = /^[0-9]{10,}$/;
    if (!formData.name.trim()) return "Full Name is required.";
    if (!phoneRegex.test(formData.phone))
      return "Phone must contain at least 10 digits.";
    if (!formData.gender) return "Gender is required.";
    if (!formData.nationality) return "Nationality is required.";
    if (
      formData.nationality === "Other" &&
      !formData.otherNationality.trim()
    )
      return "Please specify the other nationality.";
    return null;
  };

  // ✅ Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) return toast.error(error);

    const nationalityToSave =
      formData.nationality === "Other"
        ? formData.otherNationality
        : formData.nationality;

    const payload = { ...formData, nationality: nationalityToSave };
    delete payload.otherNationality;

    const method = editingId ? "PUT" : "POST";
    const url = editingId
      ? `${SERVER_URL}/api/distributors/${editingId}`
      : `${SERVER_URL}/api/distributors`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.ok) {
        toast.success(
          editingId
            ? "Distributor updated successfully!"
            : "Distributor added successfully!"
        );
        resetForm();
        fetchDistributors();
      } else toast.error(data.error || "Failed to save distributor.");
    } catch {
      toast.error("Server error while saving distributor.");
    }
  };

  // ✅ Edit Mode
  const handleEdit = (distributor) => {
    setFormData({
      ...distributor,
      otherNationality: distributor.nationality,
    });
    setEditingId(distributor._id);
    toast("Editing mode activated ✏️");
  };

  // ✅ Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this distributor?"))
      return;
    try {
      const res = await fetch(`${SERVER_URL}/api/distributors/${id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Distributor deleted successfully!");
        fetchDistributors();
      } else toast.error(data.error || "Failed to delete.");
    } catch {
      toast.error("Server error while deleting distributor.");
    }
  };

  // ✅ Reset
  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: "",
      dob: "",
      dor: "",
      phone: "",
      gender: "",
      idNumber: "",
      nationality: "",
      otherNationality: "",
    });
  };

  // ✅ Pagination
  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <div className="p-6 bg-white rounded-xl shadow-md">
      {/* 🔍 Header + Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-3">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          Manage Distributors
          <RefreshCcw
            onClick={fetchDistributors}
            className={`cursor-pointer text-blue-600 ${
              loading ? "animate-spin" : "hover:rotate-180"
            } transition-transform`}
            size={20}
          />
        </h2>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-gray-50 w-full md:w-1/3">
          <Search size={18} className="text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, phone, or nationality..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-transparent outline-none text-gray-700"
          />
        </div>
      </div>

      {/* ➕ Add/Edit Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6"
      >
        <div className="grid md:grid-cols-3 gap-4">
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <input
            type="number"
            name="phone"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />

          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>

          <input
            type="number"
            name="idNumber"
            placeholder="ID Number"
            value={formData.idNumber}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />

          <input
            type="date"
            name="dob"
            placeholder="Date of Birth"
            value={formData.dob}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />

          <input
            type="date"
            name="dor"
            placeholder="Date of Registration"
            value={formData.dor}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />

          <select
            name="nationality"
            value={formData.nationality}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Select Nationality</option>
            <option value="Kenyan">Kenyan</option>
            <option value="Ugandan">Ugandan</option>
            <option value="Tanzanian">Tanzanian</option>
            <option value="Rwandan">Rwandan</option>
            <option value="Other">Other</option>
          </select>

          {/* Conditional Other Nationality Input */}
          {formData.nationality === "Other" && (
            <input
              type="text"
              name="otherNationality"
              placeholder="Specify Other Nationality"
              value={formData.otherNationality}
              onChange={handleChange}
              className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-1"
            />
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button
            type="submit"
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            <Plus size={16} />
            {editingId ? "Update Distributor" : "Add Distributor"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {/* 📋 Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Gender</th>
              <th className="px-4 py-2">Nationality</th>
              <th className="px-4 py-2">DOB</th>
              <th className="px-4 py-2">DOR</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.map((d) => (
              <tr key={d._id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-2">{d.name}</td>
                <td className="px-4 py-2">{d.phone}</td>
                <td className="px-4 py-2">{d.gender}</td>
                <td className="px-4 py-2">{d.nationality}</td>
                <td className="px-4 py-2">{d.dob}</td>
                <td className="px-4 py-2">{d.dor}</td>
                <td className="px-4 py-2 flex gap-3">
                  <button
                    onClick={() => handleEdit(d)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(d._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredDistributors.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            No distributors found.
          </div>
        )}
      </div>

      {/* 🔢 Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          {[...Array(totalPages).keys()].map((num) => (
            <button
              key={num}
              onClick={() => handlePageChange(num + 1)}
              className={`px-3 py-1 rounded-lg border ${
                currentPage === num + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {num + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ManageDistributors;
